import type {
  CreateJobFromTemplateRequest,
  DataSyncJob,
} from './validations/types';

import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApiNoConversion } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { queryKeysDataSyncJob } from './queryKeys';
import {
  parseDataSyncResponse,
  rawEntityResponseSchema,
} from './responseShapes';
import { jobFromApiKeepingUserKeys } from './userKeyedFields';
import { createJobFromTemplateSchema } from './validations';

/**
 * Creates a job and its tasks from a template.
 *
 * The request is built by hand rather than case-converted, because `params` is
 * keyed by the template's own field names. Today those are all snake_case, which
 * conversion would leave alone, but they are the template's to choose and a
 * camelCase one would be rewritten into a key the backend does not know.
 */
export const createDataSyncJobFromTemplate = async (
  data: CreateJobFromTemplateRequest,
): Promise<DataSyncJob> => {
  const validated = createJobFromTemplateSchema.parse(data);

  const response = await vestigasApiNoConversion
    .post(ENDPOINT.DATA_SYNC.CREATE_JOB_FROM_TEMPLATE(), {
      json: {
        language: validated.language,
        name: validated.name,
        params: validated.params,
        task_keys: validated.taskKeys,
        template_uid: validated.templateUid,
      },
    })
    .json<unknown>();

  return jobFromApiKeepingUserKeys(
    parseDataSyncResponse(
      rawEntityResponseSchema,
      response,
      'job created from template',
    ),
  );
};

/**
 * Identifies the create so anything outside the form can tell one is running,
 * without the form having to report its own state upwards.
 */
export const MUTATION_KEY_CREATE_JOB_FROM_TEMPLATE = [
  'createDataSyncJobFromTemplate',
] as const;

/**
 * React Query mutation hook for creating a job from a template.
 */
export const useMutationCreateJobFromTemplate = (
  options?: Omit<
    UseMutationOptions<DataSyncJob, Error, CreateJobFromTemplateRequest>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('dataSync');

  return useMutation({
    ...options,
    mutationFn: createDataSyncJobFromTemplate,
    mutationKey: MUTATION_KEY_CREATE_JOB_FROM_TEMPLATE,
    /*
     * Composed with the caller's onSettled so the invalidation cannot be
     * overridden away by the options spread, and so a rejected create still
     * refreshes the list. TanStack Query v5 passes five arguments: the fourth
     * is whatever onMutate returned, the fifth the MutationFunctionContext.
     */
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysDataSyncJob.base(),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
    async onSuccess(...args) {
      ToastService.success(t(($) => $.toast.templateJobCreated));

      await options?.onSuccess?.(...args);
    },
  });
};
